import React from "react";
// import prettier from "prettier";

import {
  Divider,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import { InvoiceStatus } from "../../../utils/constants";
import {
  getFilePublicURL,
  getKeyFilename,
  getPhotoUrl,
} from "../../../utils/fileUtils";

import { getCustomFieldsTable } from "../../../components/common/CustomFields";

import { Ledger } from "./Ledger";
import InvoiceShowInboundBlock from "./InvoiceShowInboundBlock";
import InvoiceShowOutboundBlock from "./InvoiceShowOutboundBlock";

const useStyles = makeStyles((theme) => ({
  "@global": {
    "@media print": {
      "html, body": {
        backgroundColor: "#fff !important",
      },
    },
  },
  root: {
    padding: "0px",
    "@media print": {
      display: "block",
      minWidth: "1200px",
      margin: "0",
      padding: "50px !important",
      backgroundColor: "#fff !important",
      color: "#000 !important",
      // Remove any theme-provided dark backgrounds in printed invoice output
      "& table, & thead, & tbody, & tr, & th, & td": {
        backgroundColor: "#fff !important",
        color: "#000 !important",
      },
    },
  },
  logo: {
    margin: "0 auto 0",
    width: "auto",
    maxWidth: "90%",
    height: "auto",
    minHeight: "5em",
    maxHeight: "5em",
    borderRadius: "6px",
  },
  text: {
    "@media print": {
      color: "#000",
      backgroundColor: "#fff !important",
    },
  },
  footerBreak: {
    "@media print": {
      breakBefore: "page",
      pageBreakBefore: "always",
    },
  },
}));

const HeaderBlock = ({ record, ...props }) => {
  const classes = useStyles();
  const logoImage = window.appUser?.reloadYard?.logoImage;
  const logoURL = logoImage ? getPhotoUrl(logoImage.key, 128) : null;

  return (
    <Table style={{ width: "100%" }} className={classes.text}>
      <TableHead>
        <TableRow>
          <TableCell style={{ textAlign: "left" }}>
            {logoURL && (
              <img alt="Yard Logo" src={logoURL} className={classes.logo} />
            )}
          </TableCell>
          <TableCell style={{ textAlign: "center" }} className={classes.text}>
            <Typography variant="h5"> Invoice No: </Typography>
            <Typography variant="h6"> {record.invoiceNumber} </Typography>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            <Typography variant="h5"> Invoice Date: </Typography>
            <Typography variant="h6">
              {" "}
              {new Date(record.createdAt).toLocaleDateString()}{" "}
            </Typography>
          </TableCell>
        </TableRow>
      </TableHead>
    </Table>
  );
};

const FromToBlock = ({ record, ...props }) => {
  const classes = useStyles();
  const reloadYard = record.reloadYard;
  const remittanceAddress = reloadYard.remittanceAddress
    ? reloadYard.remittanceAddress
    : reloadYard;
  const toAddress = record.account?.billingAddress;

  return (
    <Table style={{ width: "100%" }}>
      <TableHead>
        <TableRow>
          <TableCell style={{ textAlign: "left" }} className={classes.text}>
            <Typography variant="h5"> Remit To: </Typography>
          </TableCell>
          <TableCell style={{ textAlign: "left" }} className={classes.text}>
            <Typography variant="h5"> Bill To: </Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell style={{ textAlign: "left" }} className={classes.text}>
            <strong> {remittanceAddress.name} </strong> <br />
            {remittanceAddress.street} <br />
            {remittanceAddress.city} {remittanceAddress.state}{" "}
            {remittanceAddress.zipCode} <br />
            Phone: {record.reloadYard.phone} <br />
          </TableCell>
          <TableCell style={{ textAlign: "left" }} className={classes.text}>
            <strong> {record.accountName || toAddress?.name} </strong> <br />
            {record.contact ? (
              <>
                Attn: {record.contact.name} <br />{" "}
              </>
            ) : (
              ""
            )}
            {toAddress?.street} <br />
            {toAddress?.city} {toAddress?.state} {toAddress?.zipCode} <br />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

const InfoBlock = ({ record, ...props }) => {
  const classes = useStyles();

  return (
    <div style={{ width: "100%" }}>
      <Table id="InfoBlock1" key="InfoBlock1" style={{ width: "100%" }}>
        <TableBody>
          <TableRow>
            <TableCell style={{ textAlign: "center" }} className={classes.text}>
              <Typography variant="h5"> Due Date: </Typography>
              {new Date(record.dueDate).toLocaleDateString()} <br />
            </TableCell>
            <TableCell style={{ textAlign: "center" }} className={classes.text}>
              {record.status === InvoiceStatus.PAID ? (
                <Typography variant="h5"> PAID / COMPLETED </Typography>
              ) : (
                <>
                  <Typography variant="h5"> Terms: </Typography>
                  {record.paymentTerms} <br />
                </>
              )}
            </TableCell>
            <TableCell style={{ textAlign: "center" }} className={classes.text}>
              <Typography variant="h5"> PO Number: </Typography>
              {record.poNumber} <br />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

const LedgerBlock = ({ record, ...props }) => {
  // using Grid instead of table does not require classes for text
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {" "}
        <Typography variant="h6"> Services &amp; Charges: </Typography>{" "}
      </Grid>
      <Grid item xs={12}>
        <Ledger record={record} />
      </Grid>
      <Grid item xs={12}>
        {getCustomFieldsTable(
          record?.account?.invoiceCustomFields,
          record.customFields,
        )}
      </Grid>
    </Grid>
  );
};

const FooterBlock = ({ record, ...props }) => {
  const classes = useStyles();
  if (record.inboundShipment)
    return (
      <div className={classes.footerBreak}>
        <InvoiceShowInboundBlock record={record} />
      </div>
    );
  if (record.outboundShipment)
    return (
      <div className={classes.footerBreak}>
        <InvoiceShowOutboundBlock record={record} />
      </div>
    );
  return null;
};

const AttachmentsBlock = ({ record }) => {
  const attachments =
    record.attachments?.length > 0 ? record.attachments : null;

  if (!attachments) return <></>;
  return (
    <div style={{ width: "100%" }}>
      <div>
        <Divider />
      </div>
      <Table id="invoiceDocs" key="invoiceDocs" style={{ width: "100%" }}>
        <TableBody>
          {attachments?.map((row) => (
            <TableRow>
              <TableCell style={{ textAlign: "left" }}>
                {row.key && (
                  <a
                    href={getFilePublicURL(row.key)}
                    style={{
                      margin: "0 auto",
                      width: "auto",
                      maxWidth: "90%",
                    }}
                  >
                    {getKeyFilename(row.key)}
                  </a>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const PrintableInvoiceBlock = ({
  record,
  encloseAttachments,
  ...props
}) => {
  const classes = useStyles();

  if (!record) return null;
  return (
    <Grid
      container
      spacing={1}
      alignItems="center"
      justifyContent="center"
      className={classes.root}
    >
      <Grid item xs={12} align="center">
        <HeaderBlock record={record} {...props} />
        <Divider />
        <FromToBlock record={record} {...props} />
        <Divider />
        <InfoBlock record={record} {...props} />
        <Divider />
        <span>&nbsp;</span>
        <LedgerBlock record={record} {...props} />
        <FooterBlock record={record} {...props} />
        {encloseAttachments && <AttachmentsBlock record={record} />}
      </Grid>
    </Grid>
  );
};
